import { WIX_CHAT } from '@wix/app-definition-ids';
import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import type { IntentInstallOptions } from '@wix/editor-platform-host-integration-apis';
import { uniq } from 'lodash';
import {
  PlatformApiMethods,
  PlatformContext,
} from '../../../types/platformApi';
import { UnifiedComponentsCollection } from '../../../types/unifiedComponents';
import * as appMarketService from '../../utils/appMarketService';
import { getAppType } from '../../utils/appMarketUtils';
import { IExperimentsApiAdapter } from '../../utils/experimentsApiAdapter';
import { getPendingApps } from '../../utils/pendingAppsService';
import { ApplicationsInstaller } from './ApplicationsInstaller/ApplicationsInstaller';
import { SerialInstallationStrategy } from './ApplicationsInstaller/SerialInstallationStrategy';
import { BIManager, IntentInstallBIEventStep } from './BIManager';
import { IntentInstallStore } from './IntentInstallStore';
import { PanelsManager } from './PanelsManager';
import { ProgressBarManager } from './ProgressBarManager';
import {
  PRE_INTENT_INSTALL_MODAL_EXPERIMENT,
  WIDGET_APPS_CONFIG_EXPERIMENT,
} from './constants';
import { IntentInstallErrorCode, createIntentInstallError } from './errors';
import { getIntentAppInfo } from './helpers';

type GetBiExtrasOptions = {
  appDefinitionId?: string;
};

const canInstallApp =
  (options: IntentInstallOptions) => (appData: EditorClientSpecMapEntry) => {
    if (options.shouldInstallWidgets) {
      return true;
    }
    // Temporary check for first phase of pending to silent.
    // Chat is a widget app that can be installed with the silent flow.
    // In the next phase, all apps would be able to install through silent, so the filter would be removed.
    const isChat = appData.appDefinitionId === WIX_CHAT;

    const type = getAppType(appData);
    return isChat || type !== 'tpaWidget';
  };

const getIntentApp = (ignoredApps: string[]): string | undefined => {
  const intentApp = getIntentAppInfo()?.appDefId;

  if (!intentApp || ignoredApps.includes(intentApp)) {
    return;
  }

  return intentApp;
};

export class IntentInstallServices {
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
  ) {}

  private intentInstallStore = new IntentInstallStore({
    options: {},
    appDefinitionIds: [],
  });

  private progressBarManager: ProgressBarManager | null = null;
  private panelsManager: PanelsManager | null = null;

  private applicationsInstaller = new ApplicationsInstaller();

  public async intentInstall(
    context: PlatformContext,
    options: IntentInstallOptions = {},
  ) {
    if (this.intentInstallStore.isRunning()) {
      throw createIntentInstallError(
        IntentInstallErrorCode.IntentInstallIsAlreadyRunning,
      );
    }

    const appsToInstall = options.appsToInstall ?? [];
    const ignoredApps = options.ignoredApps ?? [];

    const intentApp = getIntentApp(ignoredApps);

    const shouldInstallPending = options.shouldInstallPending ?? true;

    const pendingApps = shouldInstallPending
      ? getPendingApps(this.documentServicesAPI, canInstallApp(options))
      : [];
    const pendingAppDefs = pendingApps.map((app) => app.appDefinitionId);

    const ignoredAppsSet = new Set(ignoredApps);

    const allAppsToInstall = uniq(
      [...appsToInstall, ...pendingAppDefs].filter((appDefId) => {
        const isIntentApp = appDefId === intentApp;
        const isIgnoredApp = ignoredAppsSet.has(appDefId);

        return !isIntentApp && !isIgnoredApp;
      }),
    ).concat(intentApp ? [intentApp] : []); // so we keep intent app as the last one

    const appDefIdsToInstall = allAppsToInstall.filter(
      (appDefId) => !this.documentServicesAPI.platform.isAppActive(appDefId),
    );

    this.intentInstallStore = new IntentInstallStore({
      options,
      appDefinitionIds: appDefIdsToInstall,
      intentApp,
    });
    this.progressBarManager = new ProgressBarManager(
      this.platformApiMethods,
      this.intentInstallStore,
    );
    const biManager = new BIManager(this.intentInstallStore);

    biManager.reportIntentInstallBegin(IntentInstallBIEventStep.CHECK_INSTALL);

    if (!appDefIdsToInstall.length) {
      return;
    }

    this.intentInstallStore.setRunning(true);

    biManager.reportIntentInstallBegin(IntentInstallBIEventStep.BEGIN_INSTALL);

    // based on the options we can decide which installation strategy to use
    this.applicationsInstaller.setInstallationStrategy(
      new SerialInstallationStrategy(
        this.platformApiMethods,
        this.documentServicesAPI,
        this.experimentsAPI,
        this.intentInstallStore,
        this.progressBarManager,
      ),
    );

    this.panelsManager = new PanelsManager(
      this.platformApiMethods,
      this.intentInstallStore,
      this.progressBarManager,
    );

    const appDefIds = this.intentInstallStore.getAppsToInstall();

    this.progressBarManager.openInitialProgressBar();

    try {
      const { apps } = await appMarketService.getAppsAppMarketData(appDefIds);
      this.intentInstallStore.setAppMarketData(apps);

      if (
        this.experimentsAPI.enabled(PRE_INTENT_INSTALL_MODAL_EXPERIMENT) &&
        !options.isSilent
      ) {
        await this.panelsManager.openPreIntentInstallModal();
      }

      this.progressBarManager.startFakeProgressBarForCurrentApp();

      await this.applicationsInstaller.install(context, appDefIds, options);
    } catch (e: unknown) {
      throw createIntentInstallError(IntentInstallErrorCode.IntentInstallFailed)
        .withParentError(e as Error)
        .withApplicationsToInstall(appDefIdsToInstall)
        .withIntentApp(intentApp)
        .withIsSilent(options.isSilent);
    } finally {
      this.intentInstallStore.setRunning(false);

      this.progressBarManager.closeProgressBar();
      this.platformApiMethods.editor.updatePendingAppsNotification?.();
      this.panelsManager.openBusinessPanel();
      this.panelsManager.openPlatformFailedAlert();
    }

    this.platformApiMethods.editor.saveInBackground?.();
    // TODO(intent-install): post-install actions
  }

  public isIntentInstallRunning() {
    return this.intentInstallStore.isRunning();
  }

  public isIntentInstallRunningInSilentMode() {
    return this.isIntentInstallRunning() && this.intentInstallStore.isSilent();
  }

  public shouldUseWidgetAppsConfiguration() {
    if (!this.experimentsAPI.enabled(WIDGET_APPS_CONFIG_EXPERIMENT)) {
      return false;
    }

    return (
      this.intentInstallStore.isRunning() && !this.intentInstallStore.isSilent()
    );
  }

  public async setupIntentInstallWidgetsConfigFromUnifiedCollection(
    collection: UnifiedComponentsCollection,
    appName: string,
  ): Promise<void> {
    if (!this.shouldUseWidgetAppsConfiguration()) {
      return;
    }

    const widgets = Object.values(collection.widgets);
    const pages = Object.values(collection.pages);

    const filteredWidgets = widgets.filter(
      (widget) => widget.installation?.base?.essential,
    );

    this.intentInstallStore.setWidgetsConfiguration(
      (await this.panelsManager?.openWidgetAppsIntentInstallModal(
        filteredWidgets,
        pages,
        appName,
      )) ?? {},
    );
  }

  public getWidgetsConfiguration() {
    return this.intentInstallStore.getWidgetsConfiguration();
  }

  public getBIExtras(options?: GetBiExtrasOptions) {
    if (!this.intentInstallStore.isRunning()) {
      return {};
    }

    const appType = (() => {
      if (!options?.appDefinitionId) {
        return;
      }

      return this.intentInstallStore.getIntentApp() === options.appDefinitionId
        ? 'INTENT'
        : 'DEFAULT';
    })();

    return {
      installationId: this.intentInstallStore.getInstallationId(),
      origin: this.intentInstallStore.getOriginType(),
      ...(appType ? { appType } : {}),
    };
  }
}
